import { useInputWithDebounce } from 'customHook/useInputWithDebounce';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { BusinessesService } from 'service/businesses.service';
import { ResponseType } from './types';

export const useContacts = (id: string) => {
  const [data, setData] = useState<ResponseType | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(10);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const { value, debouncedValue, handleChange, clearInput } = useInputWithDebounce();

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await BusinessesService.getContacts(id, {
        page: currentPage + 1,
        limit: limit,
        search: debouncedValue,
      });
      setData(res);
      setTotalCount(res.total || 0);
      setTotalPages(res.totalPages || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedValue, limit, id]);

  useEffect(() => {
    if (id) {
      fetchContacts();
    }
  }, [id, currentPage, limit, debouncedValue, fetchContacts]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRowsChange = (newRows: number) => {
    setLimit(newRows);
    setCurrentPage(0);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
    handlePageChange(0);
  };

  return {
    data,
    loading,
    currentPage,
    handlePageChange,
    handleRowsChange,
    totalCount,
    totalPages,
    limit,
    handleSearch,
    value,
    debouncedValue,
    clearInput,
    handleChange,
    fetchContacts,
  };
};
